'use client';

import { GeoPointType, ProductType } from '@/@types';
import { UIComponent } from '@/components/core/components';
import { distanceBetweenGeopoints } from '@/utils/geo';
import { useLingui } from '@lingui/react';
import { Avatar, Button, Icon } from '@/components/core';
import useBookmark from '@/components/core/hooks/use-bookmark';
import { useAddCollectionContext } from '@/components/context/add-to-collection';
import InternalLink from '@/components/core/components/organism/internal-link';
import { subcategories } from '@/@constants';
import { msg } from '@lingui/core/macro';
import { useLocalizedLinks } from '@/components/core/hooks';
import { useUser } from '@/components/context/user';
import clsx from 'clsx';
import { MouseEventHandler } from 'react';
import { isProductOfType } from '@/services/api/parsers/utils';
import { Element, Image } from '@/components/core/components/atoms';
import * as styles from './place-details.styles';
import { useDownloadApp } from '../../../download-app-modal/download-app-modal';

const PlaceDetails: UIComponent<{ place?: ProductType; currentPosition?: GeoPointType; onClose?: MouseEventHandler<HTMLElement> }> = ({
  className,
  place: placeProp,
  currentPosition,
  onClose,
}) => {
  const { i18n } = useLingui();
  const { isLogged } = useUser();
  const place = useBookmark(placeProp);
  const { addToCollection } = useAddCollectionContext();
  const links = useLocalizedLinks();
  const downloadApp = useDownloadApp();

  if (!place || !isProductOfType(place, ['restaurants', 'places'])) {
    return null;
  }
  let distance = currentPosition && distanceBetweenGeopoints(place.extraInfo.location?.geo || place.geo!, currentPosition);
  let distanceUnit = 'meter';

  if (distance && distance > 1000) {
    distanceUnit = 'kilometer';
    distance /= 1000;
  }

  const firstComment = place.comments?.at(0) || place.recommendations.edges?.at(0)?.node;
  const fistCommentUser = firstComment?.user;
  const address = place.address || place.extraInfo.location?.fullAddress;

  return (
    <div className={clsx(styles.root, className)}>
      <div className={styles.grid}>
        <div className={styles.image}>
          <Image src={(place.media || []).at(0)?.source || place.image!} alt={place.name} />
          <Button
            variant="tertiary"
            className={styles.bookmark}
            onClick={() => {
              if (isLogged) {
                addToCollection(place);
                return;
              }
              downloadApp('bookmark');
            }}
          >
            <Icon name={place.isSaved ? 'bookmark-fill' : 'bookmark'} />
          </Button>
        </div>
        <div className={styles.title}>
          <h3 className={styles.name}>
            <InternalLink href={links.product(place)}>{place.name}</InternalLink>
          </h3>
          <p className={styles.distance}>
            {i18n._(
              subcategories.restaurants.concat(subcategories.places).find((x) => {
                return x.id === place.oldSuncategory;
              })?.title || msg`Others`,
            )}

            {distance && (
              <>
                {' · '}
                {new Intl.NumberFormat(i18n.locale, { style: 'unit', unit: distanceUnit, maximumFractionDigits: 0 }).format(distance)}
              </>
            )}
          </p>
        </div>
        <div className={styles.info}>
          {address && (
            <a className={styles.address} target="_blank" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`}>
              {address}
            </a>
          )}
          {firstComment && fistCommentUser && (
            <div>
              <div className="flex gap-4">
                <Avatar size={32} src={fistCommentUser.avatar} level={fistCommentUser.level?.name} />
                {fistCommentUser.nickname}
              </div>
              <Element lines={2}>{firstComment.message}</Element>
            </div>
          )}
        </div>
        {onClose && (
          <Button className={styles.close} onClick={onClose} variant="tertiary">
            <Icon name="close" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default PlaceDetails;
